/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { CheckCircle, Warning, InfoCircle, Error } from '../../assets/icons';
import { VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { randomId } from '../../util/randomId';
import IconButton from '../IconButton/iconButton';

export type AlertProps = {
  variant: 'success' | 'info' | 'warning' | 'error';
  isDismissible?: boolean;
  showIcon?: boolean;
  titleSize?: 'large' | 'small' | 'none';
  title?: string;
  description?: React.ReactNode;
  id?: string;
  content?: React.ReactNode;
  closeAriaLabel?: string;
  handleClose?: React.MouseEventHandler<HTMLButtonElement | HTMLInputElement>;
  className?: string;
};

const alertVariants = cva(
  [
    'preflight',
    'ds-flex',
    'ds-w-full',
    'ds-flex-row',
    'ds-justify-between',
    'ds-rounded',
    // padding is changing because of introducing the icon button component
    'ds-pl-5',
    'ds-pr-[14px]',
    'ds-py-4',
    '!ds-font-sans'
  ],
  {
    variants: {
      variant: {
        success: ['ds-bg-success-green-lightest', 'ds-text-success-green-darkest'],
        info: ['ds-bg-information-blue-lightest', 'ds-text-information-blue-darkest'],
        warning: ['ds-bg-warning-yellow-lightest', 'ds-text-neutrals-darker'],
        error: ['ds-bg-danger-red-lightest', 'ds-text-danger-red-darkest']
      }
    }
  }
);
interface AlertVariants extends VariantProps<typeof alertVariants> {}
const alert = (variants: AlertVariants) => twMerge(alertVariants(variants));

const lighterVariants = cva('', {
  variants: {
    variant: {
      success: 'ds-text-success-green-dark',
      info: 'ds-text-information-blue-dark',
      warning: 'ds-text-warning-yellow-darkest',
      error: 'ds-text-danger-red-dark'
    }
  }
});

const lighter = (variants: AlertVariants) => twMerge(lighterVariants(variants));

const darkestVariants = cva('', {
  variants: {
    variant: {
      success: ['ds-text-success-green-darkest', 'ds-stroke-success-green-darkest'],
      info: ['ds-text-information-blue-darkest', 'ds-stroke-information-blue-darkest'],
      warning: ['ds-text-neutrals-darker', 'ds-stroke-neutrals-darker'],
      error: ['ds-text-danger-red-darkest', 'ds-stroke-danger-red-darkest']
    }
  }
});
const darkest = (variants: AlertVariants) => twMerge(darkestVariants(variants));

const Alert = ({
  variant = 'info',
  isDismissible = true,
  showIcon = true,
  titleSize = 'large',
  title = '',
  description = '',
  content = undefined,
  closeAriaLabel = 'Close button',
  id = 'alert_' + randomId().toString(),
  handleClose,
  className = '',
  ...props
}: AlertProps) => {
  const icon: { [key: string]: React.ReactNode } = {
    success: <CheckCircle className={darkest({ variant })} />,
    info: <InfoCircle className={darkest({ variant })} />,
    warning: <Warning className={darkest({ variant })} />,
    error: <Error className={darkest({ variant })} />
  };

  const addMarginBetweenDescriptionAndContent = () => {
    const titleExists = titleSize !== 'none' || title !== '';
    return content !== undefined && description !== '' && !titleExists;
  };

  const titleContainerClasses = () => {
    return titleSize === 'large'
      ? 'ds-flex-col ds-flex ds-gap-3'
      : titleSize === 'small'
        ? 'ds-flex-row ds-flex ds-gap-3'
        : 'ds-flex';
  };

  return (
    <div id={id} className={twMerge(alert({ variant }), className)} {...props}>
      <div className="ds-flex ds-flex-row">
        {showIcon && (
          <div
            className={`ds-flex ds-pr-2 ${titleSize === 'large' && title !== '' && 'ds-h-[1.75rem] ds-items-center'}`}
          >
            {icon[variant]}
          </div>
        )}
        <div
          className={`ds-flex ds-flex-col ${titleSize !== 'none' && title !== '' ? 'ds-gap-3' : 'ds-justify-center'}`}
        >
          <div className={titleContainerClasses()}>
            {titleSize !== 'none' && title !== '' && (
              <h1
                className={
                  titleSize === 'large' ? 'ds-text-xl ds-font-semibold' : 'ds-hidden'
                }
              >
                {title}
              </h1>
            )}
            <div className={lighter({ variant })}>
              {titleSize !== 'none' && title !== '' && (
                <span
                  className={`${darkest({ variant })} ${titleSize === 'small' && title !== '' ? 'ds-pr-2 ds-text-base ds-font-semibold' : 'ds-hidden'}`}
                >
                  {title}
                </span>
              )}
              <span
                className={`${titleSize === 'none' || title === '' ? 'ds-mb-3' : ''}`}
              >
                {description}
              </span>
            </div>
          </div>
          {content && (
            <div
              className={`${lighter({ variant })} ${addMarginBetweenDescriptionAndContent() ? 'ds-mt-3' : ''}`}
            >
              {content}
            </div>
          )}
        </div>
      </div>
      {isDismissible === true && (
        <div className="-ds-mt-2 ds-h-5">
          <IconButton
            variant="default"
            iconSize="small"
            iconName="close"
            hasTooltip={false}
            label={closeAriaLabel}
            disabled={false}
            iconColor={darkest({ variant })}
            onClick={
              handleClose as unknown as React.MouseEventHandler<
                HTMLButtonElement | HTMLInputElement
              >
            }
          />
        </div>
      )}
    </div>
  );
};

export default Alert;
